import {
    defaultIntakeForm,
    loadIntakeForm,
    persistIntakeForm,
    type PreContractIntakeForm,
    type PreContractSiteContext,
} from './preContractTypes';

type ReloadHygieneWindow = Window & {
    __oneDrawPreContractReloadHygiene?: {
        siteContext: PreContractSiteContext | null;
    };
};

const PRESERVED_PRE_CONTRACT_KEY_PREFIXES = [
    'preContractAiDesignOptionsCache',
    'preContractFullModuleLayouts',
];

type ViewportState = Pick<
    PreContractIntakeForm,
    | 'roofSelections'
    | 'includeGreyPanels'
    | 'includePurplePanels'
    | 'fillOrientations'
    | 'auroraAvailable'
    | 'hoverAvailable'
    | 'preferredDesignTool'
    | 'moduleTypeName'
    | 'moduleTypeWattage'
    | 'treeCount'
    | 'siteTrees'
    | 'roofModuleLayouts'
    | 'systemUsageKwh'
    | 'roofSlopeLowTotal'
    | 'roofSlopeNormalTotal'
    | 'solarDesignRevision'
    | 'appliedAiDesignTitle'
>;

function isPageReload(): boolean {
    if (typeof performance === 'undefined') return false;
    const navigation = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
    return navigation?.type === 'reload';
}

function viewportStateFromForm(form: PreContractIntakeForm | null): ViewportState | null {
    if (!form) return null;
    return {
        roofSelections: form.roofSelections,
        includeGreyPanels: form.includeGreyPanels,
        includePurplePanels: form.includePurplePanels,
        fillOrientations: form.fillOrientations,
        auroraAvailable: form.auroraAvailable,
        hoverAvailable: form.hoverAvailable,
        preferredDesignTool: form.preferredDesignTool,
        moduleTypeName: form.moduleTypeName,
        moduleTypeWattage: form.moduleTypeWattage,
        treeCount: form.treeCount,
        siteTrees: form.siteTrees,
        roofModuleLayouts: form.roofModuleLayouts,
        systemUsageKwh: form.systemUsageKwh,
        roofSlopeLowTotal: form.roofSlopeLowTotal,
        roofSlopeNormalTotal: form.roofSlopeNormalTotal,
        solarDesignRevision: form.solarDesignRevision,
        appliedAiDesignTitle: form.appliedAiDesignTitle,
    };
}

/**
 * A full browser reload starts a fresh pre-contract workflow. AI results and the
 * layout needed to restore the persistent oneDRAW viewport are intentionally retained.
 */
export function clearPreContractSessionOnReload(
    salesForceId: string,
    siteContext?: PreContractSiteContext | null,
): PreContractSiteContext | null {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        return siteContext ?? null;
    }
    const reloadWindow = window as ReloadHygieneWindow;
    const applied = reloadWindow.__oneDrawPreContractReloadHygiene;
    if (applied) return applied.siteContext ?? siteContext ?? null;
    if (!isPageReload()) return siteContext ?? null;

    const reloadSiteContext = siteContext ?? null;
    reloadWindow.__oneDrawPreContractReloadHygiene = { siteContext: reloadSiteContext };

    const sfid = salesForceId.trim();
    const viewportState = sfid ? viewportStateFromForm(loadIntakeForm(sfid)) : null;

    try {
        const keysToRemove: string[] = [];
        for (let index = 0; index < sessionStorage.length; index += 1) {
            const key = sessionStorage.key(index);
            if (
                key?.startsWith('preContract') &&
                !PRESERVED_PRE_CONTRACT_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))
            ) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
        return reloadSiteContext;
    }

    if (sfid && viewportState && siteContext) {
        persistIntakeForm(sfid, {
            ...defaultIntakeForm(siteContext),
            ...viewportState,
        });
    }

    return reloadSiteContext;
}
